import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initSmoothScroll } from './smoothScroll.js';

gsap.registerPlugin(ScrollTrigger);

/**
 * GSAP Motion Engine — Apple spring curves, light theme
 */
export function initGSAPAnimations() {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // 1. Native scroll
  initSmoothScroll();

  // 2. Preloader sequence (light background)
  const preloaderEl = document.getElementById('preloader');
  if (preloaderEl && !reducedMotion) {
    document.documentElement.style.overflow = 'hidden';

    const preloaderTl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: () => {
        document.documentElement.style.overflow = '';
        if (preloaderEl) preloaderEl.style.display = 'none';
        runHeroIntro();
      }
    });

    preloaderTl
      .fromTo('.preloader-brand', { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35 })
      .fromTo('.preloader-title span',
        { yPercent: 100, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.5, stagger: 0.08 },
        '-=0.15'
      )
      .fromTo('.preloader-progress i', { scaleX: 0 }, { scaleX: 1, duration: 0.6, ease: 'power2.inOut' }, '-=0.2')
      .fromTo('.preloader-credit', { opacity: 0 }, { opacity: 1, duration: 0.25 }, '-=0.2')
      .to('.preloader-inner', { y: -12, opacity: 0, duration: 0.3, ease: 'power2.in' }, '+=0.1')
      .to(preloaderEl, { yPercent: -100, duration: 0.55, ease: 'power4.inOut' });
  } else {
    if (preloaderEl) preloaderEl.style.display = 'none';
    runHeroIntro();
  }

  // 3. Hero entrance
  function runHeroIntro() {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.9 } });

    tl.fromTo('.ws-top-nav',
      { y: -40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6 }
    )
    .fromTo('.hero-left > *',
      { y: 24, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7, stagger: 0.08 },
      '-=0.3'
    )
    .fromTo('.hero-right .product-window',
      { y: 32, opacity: 0, scale: 0.97 },
      { y: 0, opacity: 1, scale: 1, duration: 0.9, ease: 'expo.out' },
      '-=0.6'
    );
  }

  // 4. Scroll text reveals
  if (!reducedMotion) {
    const textRevealHeadings = document.querySelectorAll('[data-text-reveal]');
    textRevealHeadings.forEach((heading) => {
      gsap.fromTo(heading,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: heading,
            start: 'top 85%',
            once: true
          }
        }
      );
    });
  }

  // 5. Subtle magnetic hover on buttons (gentle, not gimmicky)
  const primaryBtns = document.querySelectorAll('.ws-btn-primary');
  primaryBtns.forEach(btn => {
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(btn, {
        x: x * 0.08,
        y: y * 0.08,
        duration: 0.3,
        ease: 'power2.out'
      });
    });

    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, {
        x: 0,
        y: 0,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)'
      });
    });
  });
}

/**
 * Animate HUD Values
 */
export function animateHUDValue(elementId, newText, isNumber = false, numberVal = 0, prefix = '') {
  const el = document.getElementById(elementId);
  if (!el) return;

  gsap.to(el, {
    opacity: 0,
    y: -6,
    duration: 0.2,
    onComplete: () => {
      if (isNumber) {
        const obj = { val: 0 };
        gsap.to(obj, {
          val: numberVal,
          duration: 0.8,
          ease: 'power2.out',
          onUpdate: () => {
            el.textContent = `${prefix}${Math.round(obj.val).toLocaleString()}`;
          }
        });
      } else {
        el.textContent = newText;
      }
      gsap.to(el, { opacity: 1, y: 0, duration: 0.3 });
    }
  });
}
